import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { LANGS, STRINGS } from "./i18nStrings";

const LangContext = createContext(null);

const STORAGE_KEY = "pp.lang";

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    // 1. Query-param wins for SEO deep-links (Google-crawled ?lang=ar URLs).
    try {
      const qs = new URLSearchParams(window.location.search);
      const qlang = qs.get("lang");
      if (qlang && LANGS.some((l) => l.code === qlang)) {
        // Persist the choice so subsequent internal nav stays in that language.
        try { localStorage.setItem(STORAGE_KEY, qlang); } catch { /* ignore */ }
        return qlang;
      }
    } catch { /* ignore */ }
    // 2. Fall back to the previously chosen language.
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && LANGS.some((l) => l.code === saved)) return saved;
    } catch { /* ignore */ }
    return "en";
  });

  const dir = useMemo(() => LANGS.find((l) => l.code === lang)?.dir || "ltr", [lang]);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    if (dir === "rtl") document.body.classList.add("pp-rtl");
    else document.body.classList.remove("pp-rtl");
  }, [lang, dir]);

  const setLang = (code) => {
    if (!LANGS.some((l) => l.code === code)) return;
    localStorage.setItem(STORAGE_KEY, code);
    setLangState(code);
  };

  const value = useMemo(() => {
    const S = STRINGS[lang] || STRINGS.en;
    const t = (path, vars) => {
      const parts = path.split(".");
      let cur = S;
      for (const p of parts) {
        cur = cur ? cur[p] : undefined;
      }
      if (cur == null) {
        // Fall back to English
        let en = STRINGS.en;
        for (const p of parts) { en = en ? en[p] : undefined; }
        cur = en;
      }
      let out = cur == null ? path : String(cur);
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => { out = out.replace(new RegExp(`\\{${k}\\}`, "g"), String(v)); });
      }
      return out;
    };
    return { lang, setLang, dir, t, S, LANGS };
  }, [lang, dir]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used inside <LangProvider>");
  return ctx;
}
